import type { DateTime } from 'luxon'

import type { PqPhase, PrequalificationResourceSet } from '@/features/prequalification/types'

/** Which timestamp the date-range filter is measured against. */
export type ResourceSetDateField = 'created' | 'updated'

/** Relative date-range presets offered in the filters popover. */
export type ResourceSetDateRange = 'all' | 'last_24h' | 'last_7d' | 'last_30d' | 'last_90d'

/**
 * The full filter state behind the resource sets list toolbar. `search` and `status`
 * live in the toolbar itself; `dateField` and `dateRange` live in the "Filters"
 * popover. All filtering is client-side over the already loaded resource set list, so
 * the shape stays flat and serialisable.
 */
export type ResourceSetFilters = {
  /** Free-text match against the resource set name. */
  search: string
  /** Aggregate phase to keep, or `'all'`. */
  status: PqPhase | 'all'
  /** The timestamp the date range applies to. Inert while `dateRange` is `'all'`. */
  dateField: ResourceSetDateField
  dateRange: ResourceSetDateRange
}

export const DEFAULT_RESOURCE_SET_FILTERS: ResourceSetFilters = {
  search: '',
  status: 'all',
  dateField: 'created',
  dateRange: 'all',
}

/** Window, in days, for each relative range preset (`'all'` is unbounded). */
const RANGE_DAYS: Record<Exclude<ResourceSetDateRange, 'all'>, number> = {
  last_24h: 1,
  last_7d: 7,
  last_30d: 30,
  last_90d: 90,
}

function matchesSearch(resourceSet: PrequalificationResourceSet, query: string): boolean {
  if (!query) return true

  return resourceSet.name.toLowerCase().includes(query)
}

function matchesDateRange(
  resourceSet: PrequalificationResourceSet,
  filters: ResourceSetFilters,
  now: DateTime,
): boolean {
  if (filters.dateRange === 'all') return true

  const stamp = filters.dateField === 'created' ? resourceSet.requestedAt : resourceSet.lastStatusAt
  if (!stamp) return false

  return stamp >= now.minus({ days: RANGE_DAYS[filters.dateRange] })
}

/**
 * Narrows the resource set list down to the rows that satisfy every active filter.
 * `now` is injected (rather than read from the clock) so the relative date
 * ranges stay deterministic under test.
 */
export function filterResourceSets(
  resourceSets: PrequalificationResourceSet[],
  filters: ResourceSetFilters,
  now: DateTime,
): PrequalificationResourceSet[] {
  const query = filters.search.trim().toLowerCase()

  return resourceSets.filter((resourceSet) => {
    if (!matchesSearch(resourceSet, query)) return false
    if (filters.status !== 'all' && resourceSet.phase !== filters.status) return false

    return matchesDateRange(resourceSet, filters, now)
  })
}

/** Whether any filter is narrowing the list (drives the "no results" overlay). */
export function isAnyFilterActive(filters: ResourceSetFilters): boolean {
  return filters.search.trim() !== '' || filters.status !== 'all' || filters.dateRange !== 'all'
}

/** How many popover-scoped filters are set — shown as a count badge on the Filters button. */
export function activePopoverFilterCount(filters: ResourceSetFilters): number {
  let count = 0
  if (filters.dateRange !== 'all') count += 1

  return count
}
